import { AppLayout, NotificationProvider } from '@sellsen/components';

import {
  useGetCurrentUserQuery,
  useWarehouseCaseUnreadCountQuery,
} from '@sellsen/services';
import { Badge, ConfigProvider, Space } from 'antd';
import type { MenuProps } from 'antd';
import enUS from 'antd/locale/en_US';
import trTR from 'antd/locale/tr_TR';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  setAutoOrderCustomerSlId,
  setAutoOrderOwnerUsername,
} from './pages/orders/order-purchaser';
import {
  CHROME_EXTENSION_MENU_KEY,
  CHROME_WEB_STORE_URL,
  filterMenuItemsForSeller,
  getMenuUrl,
  MENU_ITEMS,
  USER_MENU_ITEMS,
} from './constants/menu-items';
import { GlobalOrderSearch } from './components/global-order-search';
import { SellerLimitBanner } from './components/seller-limit-banner';
import { SellerLimitUsage } from './components/seller-limit-usage';
import { useIsSeller } from './hooks/use-is-seller';

const WAREHOUSE_CASE_MENU_KEYS = { parent: '9', child: '9-1' } as const;

/**
 * Menü etiketlerine okunmamış rozetini ekler. Menü sabitleri statik kaldığı için rozet burada,
 * render sırasında enjekte edilir.
 */
const withWarehouseCaseBadge = (
  items: MenuProps['items'],
  unreadCount: number
): MenuProps['items'] => {
  if (!items || unreadCount <= 0) {
    return items;
  }
  const withBadge = (label: React.ReactNode) => (
    <Badge count={unreadCount} size="small" offset={[8, -2]}>
      {label}
    </Badge>
  );

  return items.map((item) => {
    if (
      !item ||
      !('key' in item) ||
      item.key !== WAREHOUSE_CASE_MENU_KEYS.parent
    ) {
      return item;
    }
    const parent = item as {
      label?: React.ReactNode;
      children?: MenuProps['items'];
    };
    return {
      ...item,
      label: withBadge(parent.label),
      children: parent.children?.map((child) =>
        child && 'key' in child && child.key === WAREHOUSE_CASE_MENU_KEYS.child
          ? {
              ...child,
              label: withBadge((child as { label?: React.ReactNode }).label),
            }
          : child
      ),
    } as NonNullable<MenuProps['items']>[number];
  });
};

export function App() {
  const navigate = useNavigate();
  const { data: user, isLoading } = useGetCurrentUserQuery();
  const isSeller = useIsSeller();
  const { i18n } = useTranslation();
  // Depo mesajları satıcı modülüdür; ASIN-only kullanıcıda uç 403 döner, sorgu hiç atılmaz.
  const { data: unreadCases } = useWarehouseCaseUnreadCountQuery(
    !isLoading && isSeller
  );

  useEffect(() => {
    setAutoOrderCustomerSlId(user?.shippinglotChildId);
    setAutoOrderOwnerUsername(user?.username);
  }, [user?.shippinglotChildId, user?.username]);

  const antdLocale = i18n.language === 'tr' ? trTR : enUS;
  // ASIN-only kullanıcı satıcı menülerini (Ürün/Envanter/Sipariş/Depo/Mağazam) görmez.
  const baseMenuItems = isLoading
    ? []
    : filterMenuItemsForSeller(MENU_ITEMS, isSeller);
  // Depo mesajlarının okunmamış rozeti hem üst menüde hem alt öğede görünür: kullanıcı menüyü
  // açmadan da yeni mesaj olduğunu görmeli.
  const menuItems = withWarehouseCaseBadge(
    baseMenuItems,
    unreadCases?.count ?? 0
  );

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: '#1890ff',
          borderRadius: 6,
        },
        components: {
          // Üst menü 8 öğeyle varsayılan 16px padding'de 1440px'e sığmıyordu; boşluk kodda
          // değil temada daraltılır ki tüm yatay menüler aynı ritmi taşısın.
          Menu: { itemPaddingInline: 12, iconMarginInlineEnd: 8 },
        },
      }}
    >
      <NotificationProvider>
        <AppLayout
          onMenuSelect={(key) => {
            if (key === CHROME_EXTENSION_MENU_KEY) {
              window.open(
                CHROME_WEB_STORE_URL,
                '_blank',
                'noopener,noreferrer'
              );
              return;
            }
            const url = getMenuUrl(key);

            if (url) {
              navigate(url);
            }
          }}
          menuItems={menuItems}
          userMenuItems={USER_MENU_ITEMS}
          user={user}
          brandTitle="SELLSEN"
          headerExtra={
            <Space size="small" align="center">
              {!isLoading && isSeller ? <GlobalOrderSearch /> : null}
              <SellerLimitUsage limits={user?.sellerLimits} />
            </Space>
          }
        >
          {/* Kota dolunca/dolmak üzereyken her sayfada görünen şerit; header halkası tek başına
              fark edilmiyordu (zeynep, Eki 2026: %100 kırmızı halka, kimse görmedi). */}
          <SellerLimitBanner />
          <Outlet />
        </AppLayout>
      </NotificationProvider>
    </ConfigProvider>
  );
}
